import {Box, TextField, Typography} from '@mui/material';
import {t} from 'i18next';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {isEmpty} from '../../../../utils/utils';
import {QueryFieldDrawer} from './QueryFieldDrawer';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';

interface IQueryFieldDefaultDrawerProps {
  open: boolean;
  initialInputValue?: string;
  onCancel?: () => void;
  onApply?: (textQuery?: string) => void;
}
const INDEX_TYPE_STANDARD = 'STANDARD';

const QueryFieldDefaultDrawer: React.FC<IQueryFieldDefaultDrawerProps> = ({
  open,
  initialInputValue = '',
  onCancel,
  onApply,
}) => {
  const [isModal, setIsModal] = useState<boolean>(open);
  const [fieldValue, setFieldValue] = useState<string>('');
  const {index} = useSelector((state: RootState) => state.query);

  const isStanderedIndex = useMemo(() => {
    return (
      (index?.type ?? '').toLowerCase() === INDEX_TYPE_STANDARD.toLowerCase()
    );
  }, [index]);
  useEffect(() => {
    if (initialInputValue) {
      setFieldValue(initialInputValue);
    }
  }, [initialInputValue]);

  useEffect(() => {
    setIsModal(open);
  }, [open]);

  const handleFieldValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setFieldValue(newValue);
    },
    []
  );

  const onCancelClicked = useCallback(() => {
    setIsModal(false);
    onCancel?.();
  }, [onCancel]);

  const onApplyClicked = useCallback(() => {
    setIsModal(false);
    onApply?.(fieldValue);
  }, [onApply, fieldValue]);

  const renderDefaultField = useMemo(() => {
    return (
      <Box className={'default-field-parent-container'}>
        <Box className={'standard-example-container'}>
          {isStanderedIndex ? (
            <>
              <span className="typo-text subhead4_size">{`${t('queryBuilder.standardIndexes')} `}</span>
              <span className="typo-text body2_size text-color">
                {`${t('queryBuilder.searchOnlyTheseFields')}:`}
              </span>
              <Typography className="typo-text body2_size text-color">
                {t('queryBuilder.standardIndexExamples')}
              </Typography>
            </>
          ) : (
            <Typography className="typo-text body2_size text-color">
              {t('queryBuilder.customIndexText')}
            </Typography>
          )}
        </Box>
        <Box>
          <Typography className="typo-text subhead4_size">
            {`${t('queryBuilder.contains')}:`}
          </Typography>
          <TextField
            variant={'standard'}
            multiline
            className={'query-field-default-textfield'}
            value={fieldValue}
            minRows={1}
            maxRows={12}
            placeholder={t('indexSearchPage.enterKeyboard')}
            margin={'none'}
            inputProps={{
              'data-testid': 'queryTextField',
              'aria-label': t('indexSearchPage.enterKeyboard'),
            }}
            InputProps={{
              disableUnderline: true,
            }}
            onChange={handleFieldValueChange}
          />
        </Box>
      </Box>
    );
  }, [fieldValue, handleFieldValueChange]);

  return (
    <QueryFieldDrawer
      open={isModal}
      headerProps={{
        title: t('queryBuilder.defaultFields'),
        isApplyDisabled: isEmpty(fieldValue),
        onCancelClicked,
        onApplyClicked,
      }}
    >
      {renderDefaultField}
    </QueryFieldDrawer>
  );
};

export {QueryFieldDefaultDrawer};
